import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';
import get from 'lodash/get';
import noop from 'lodash/noop';
import { Link } from '@/Framework/Router/Next/Link';
import { DEFAULT_EMPTY_MESSAGE } from '@/Framework/UI/Molecules/Tables/constants';
import Spinner from '@dealroadshow/uikit/core/components/Loader/Spinner';
import Paginator from '@dealroadshow/uikit/core/components/Paginator';
import styles from './linkTable.scss';
import floatStyles from '@dealroadshow/uikit/core/styles/helpers/floats.scss';
import cardStyles from '@dealroadshow/uikit/core/styles/card.scss';

interface IProps {
  containerClassName?: string,
  tableClassName?: string,
  rowClassName?: string,
  emptyMessageClassName?: string,
  emptyMessageText?: string,
  withSpinner?: boolean,
  rowDataTest?: string,

  title?: string,
  headerActionsComponent?: React.ElementType,
  showColumnsHeaders?: boolean,

  onPageChange?: (page: { selected: number }) => void,
  onItemsPerPageChange?: (params: { value: number }) => void,
  getRoutePath?: (item: any) => string | void,

  columns: any[],
  data?: any[],
  isFetching: boolean,
  totalCount?: number,
  page?: number,
  perPage?: number,
  isItemsPerPageVisible?: boolean,
  paginatorDataTest?: string,
  dataTest?: string,
}

const LinkTable = (
  {
    containerClassName = '',
    tableClassName = '',
    rowClassName = '',
    emptyMessageClassName = '',
    emptyMessageText = '',
    withSpinner = true,
    rowDataTest = '',
    dataTest = 'linkTable',

    title = '',
    headerActionsComponent: HeaderActions = null,
    showColumnsHeaders = true,

    data = [],
    columns,
    isFetching,
    totalCount = 0,
    page = 1,
    perPage = 25,
    isItemsPerPageVisible = true,
    paginatorDataTest = '',

    onPageChange = noop,
    onItemsPerPageChange = noop,
    getRoutePath = noop,
  }: IProps,
) => {
  const containerCls = cn(cardStyles.cardContainerNoSwadow, containerClassName);
  const tableCls = cn({ [styles.tableWithPaginatorContainer]: !!totalCount }, tableClassName);
  const rowCls = cn(styles.row, rowClassName);
  const paginatorCls = cn(cardStyles.cardInner, styles.paginatorContainer);

  const renderCells = useCallback(
    (rowData, rowIndex) => columns.map(
      (
        {
          name,
          className,
          cellCallback,
          width,
          minWidth,
          maxWidth,
        },
      ) => {
        const cellData = get(rowData, name);
        return (
          <div
            key={ name }
            className={ cn(styles.cell, className) }
            style={ { width, minWidth, maxWidth } }
          >
            { cellCallback ? cellCallback({ rowData, rowIndex, cellData }) : cellData }
          </div>
        );
      },
    ),
    [columns],
  );

  const renderRow = useCallback(
    (item, i) => (
      <Link
        key={ item.id || i }
        // @ts-ignore
        to={ getRoutePath(item) }
        className={ rowCls }
        dataTest={ rowDataTest ? `${ rowDataTest }_${ i }` : `linkTableRow_${ i }` }
      >
        { renderCells(item, i) }
      </Link>
    ),
    [getRoutePath, renderCells],
  );

  const columnsHeaders = useMemo(
    () => columns.map(
      (
        {
          title,
          name,
          width,
          minWidth,
          maxWidth,
          className,
        },
      ) => (
        <div
          key={ name }
          className={ cn(styles.tableHeaderCell, className) }
          style={ { width, minWidth, maxWidth } }
        >
          { title }
        </div>
      ),
    ),
    [columns],
  );

  return (
    <div className={ containerCls } data-test={ dataTest }>
      { (title || HeaderActions) && (
        <div className={ cn(cardStyles.cardInner, styles.stripe) }>
          <div className={ styles.tableHeader }>
            { title && (
              <div className={ styles.headerTitle }>
                <h3>{ title }</h3>
              </div>
            ) }
            { HeaderActions && (
              <div className={ floatStyles.pullRight }>
                <HeaderActions />
              </div>
            ) }
          </div>
        </div>
      ) }
      { withSpinner && (
        <Spinner
          overlay
          isVisible={ isFetching }
        />
      ) }
      <div className={ tableCls }>
        { showColumnsHeaders && (
          <div className={ styles.tableHeadersContainer }>
            { columnsHeaders }
          </div>
        ) }
        {
          (!data.length && !isFetching)
            ? (
              <div className={ cn(styles.emptyMessage, emptyMessageClassName) }>
                { emptyMessageText || DEFAULT_EMPTY_MESSAGE }
              </div>
            )
            : data.map(renderRow)
        }
      </div>
      <Paginator
        isItemsPerPageVisible={ isItemsPerPageVisible }
        className={ paginatorCls }
        onPageChange={ onPageChange }
        onItemsPerPageChange={ onItemsPerPageChange }
        totalCount={ totalCount }
        page={ page }
        perPage={ perPage }
        dataTest={ paginatorDataTest || 'linkTablePaginator' }
      />
    </div>
  );
};

export default LinkTable;
